<script lang="ts">
	import Drawer from './Drawer.svelte';
	import LogoIcon from './LogoIcon.svelte';
	import { readSettings, writeSettings, type Theme } from '$lib/settings';
	import { showToast } from '$lib/toast';

	let { open, onclose }: { open: boolean; onclose: () => void } = $props();

	let theme = $state<Theme>('hearth');

	const THEMES: { id: Theme; label: string; src: string; badge?: string; comingSoon?: boolean }[] = [
		{ id: 'dark', label: 'Dark', src: '' },
		{ id: 'light', label: 'Light', src: '' },
		{ id: 'hearth', label: 'Hearth', src: '/themes/hearth.jpg' },
		{ id: 'galactic', label: 'Galactic', src: '/themes/galactic.jpg' },
		{ id: 'wizardry', label: 'Wizardry', src: '/themes/wizardry.jpg' },
		{ id: 'overgrown', label: 'Overgrown', src: '/themes/overGrown.jpg' },
		{ id: 'outbreak', label: 'Outbreak', src: '/themes/outbreak.jpg' },
		{ id: 'catchers', label: 'Catchers', src: '/themes/catchers.jpg' },
		{ id: 'noir', label: 'Noir', src: '/themes/noir.jpg' }
	];

	$effect(() => {
		if (open) {
			theme = readSettings().theme;
		}
	});

	function setTheme(t: Theme) {
		theme = t;
		writeSettings({ ...readSettings(), theme });
		window.dispatchEvent(new StorageEvent('storage'));
		showToast(`Theme set to ${t}`, 'success');
	}
</script>

<Drawer title="Theme" icon="mdi:image-outline" {open} {onclose}>
	<div class="flex flex-col gap-4">
		<div class="pixel-frame relative">
			<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
			<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
			<div class="pixel-frame absolute inset-1 bg-(--bottom-nav-frame-shadow)"></div>
			<div class="pixel-frame absolute inset-1.5 bg-(--bottom-nav-frame-fill)"></div>
			<div class="relative flex flex-col gap-3 px-4 py-3">
				<div class="grid grid-cols-2 gap-2">
					{#each THEMES as t}
						<button type="button" onclick={() => !t.comingSoon && setTheme(t.id)} class="theme-tile pixel-frame relative flex-1 overflow-hidden {t.comingSoon ? 'cursor-default opacity-60' : 'cursor-pointer'}">
							{#if theme === t.id}
								<div class="pixel-frame absolute inset-0 bg-(--correct-frame-outer)"></div>
								<div class="pixel-frame absolute inset-0.5 bg-(--correct-frame-highlight)"></div>
								<div class="pixel-frame absolute inset-1 bg-(--correct-frame-shadow)"></div>
							{:else}
								<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
								<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
								<div class="pixel-frame absolute inset-1 bg-(--bottom-nav-frame-shadow)"></div>
							{/if}
							<div class="relative flex flex-col items-center gap-1 p-1.5">
								{#if t.badge}
									<div class="pixel-frame absolute top-2 right-2 z-10">
										<div class="pixel-frame absolute inset-0 bg-(--correct-frame-outer)"></div>
										<div class="pixel-frame absolute inset-0.5 bg-(--correct-frame-highlight)"></div>
										<div class="pixel-frame absolute inset-1 bg-(--correct-frame-shadow)"></div>
										<div class="pixel-frame absolute inset-1.5 bg-(--correct-frame-fill)"></div>
										<span class="relative block px-1.5 py-0.5 text-xs leading-none font-bold text-(--correct-frame-text) uppercase">{t.badge}</span>
									</div>
								{/if}
								<div class="pixel-frame relative w-full overflow-hidden" style="height: 68px;">
									{#if t.comingSoon}
										<div class="absolute inset-0 bg-black/20"></div>
										<div class="absolute inset-0 flex items-center justify-center">
											<span class="text-xs font-bold uppercase opacity-70">Coming Soon</span>
										</div>
									{:else if t.id === 'dark'}
										<div class="absolute inset-0" style="background: #0f0f0f;"></div>
										<div class="pointer-events-none absolute inset-0" style="background-image: linear-gradient(to right, transparent 0 8px, rgba(239, 68, 68, 0.35) 8px 9px, transparent 9px), repeating-linear-gradient(to bottom, transparent 0 9px, rgba(147, 197, 253, 0.18) 9px 10px);"></div>
										<div class="pointer-events-none absolute inset-0 flex items-center justify-center" data-theme={t.id}>
											<LogoIcon size={40} themeOverride={t.id} />
										</div>
									{:else if t.id === 'light'}
										<div class="absolute inset-0" style="background: #f0f0f0;"></div>
										<div class="pointer-events-none absolute inset-0" style="background-image: linear-gradient(to right, transparent 0 8px, rgba(239, 68, 68, 0.45) 8px 9px, transparent 9px), repeating-linear-gradient(to bottom, transparent 0 9px, rgba(59, 130, 246, 0.25) 9px 10px);"></div>
										<div class="pointer-events-none absolute inset-0 flex items-center justify-center" data-theme={t.id}>
											<LogoIcon size={40} themeOverride={t.id} />
										</div>
									{:else}
										<div class="absolute inset-0 bg-black/20"></div>
										<img src={t.src} alt="" class="absolute inset-0 h-full w-full object-cover" />
										<div class="pointer-events-none absolute inset-0 flex items-center justify-center" data-theme={t.id}>
											<LogoIcon size={40} themeOverride={t.id} />
										</div>
									{/if}
								</div>
								<span class="text-xs font-bold uppercase {theme === t.id ? 'text-(--correct-frame-text)' : 'text-(--bottom-nav-frame-text)'}">{t.label}</span>
							</div>
						</button>
					{/each}
				</div>
			</div>
		</div>
	</div>
</Drawer>
